import { fetchApi, fetchApiBase } from "~/lib/api";
import type { CustomerGetSongsParams, Demo, Pagination, Song } from "~/types";
import { serializeFilterParams } from "./utils";

const parseContentDispositionFilename = (
  contentDisposition: string | null,
): string | null => {
  if (!contentDisposition) return null;
  const match = contentDisposition.match(
    /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
  );
  if (match && match[1]) {
    const filename = match[1].replace(/['"]/g, "");
    return filename;
  }
  return null;
};

export const customerSongsService = {
  /**
   * Get all songs (owned + shared) for the authenticated user
   */
  async getAllSongs(params: CustomerGetSongsParams = {}) {
    const query = serializeFilterParams(params);
    const endpoint = `/customer/songs${query}`;
    return fetchApi<Pagination<Song>>(endpoint);
  },
  /**
   * Download the song for an order
   */
  async downloadSong(songId: string | number) {
    const response = await fetchApiBase(`/customer/songs/download/${songId}`);

    const blob = await response.blob();
    const contentDisposition = response.headers.get("content-disposition");
    const filename =
      parseContentDispositionFilename(contentDisposition) ||
      `melodia-${songId}.mp3`;

    const downloadUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(downloadUrl);
    document.body.removeChild(a);
  },
  /**
   * Rename a song (updates the song title in the order)
   */
  async renameSong(songId: string | number, title: string) {
    await fetchApi<{ success: boolean }>(`/customer/songs/${songId}/title`, {
      method: "PATCH",
      body: JSON.stringify({ title }),
    });
  },
  /**
   * Get the streaming URL for a song
   */
  streamUrlSong(songId: number) {
    return `/api/customer/songs/stream/${songId}`;
  },
  /**
   * Get the URL for an order's artwork.
   * Pass a version string (e.g. artworkFilePath) to bust the browser cache when artwork changes.
   */
  artworkUrl(orderId: number, version?: string | null) {
    const base = `/api/customer/songs/artwork/${orderId}`;
    return version ? `${base}?v=${encodeURIComponent(version)}` : base;
  },
};

export const adminSongsService = {
  /**
   * Upload a song file for an order
   */
  async uploadSong(orderId: number, file: File) {
    const endpoint = `/admin/songs/orders/${orderId}/song`;

    const formData = new FormData();
    formData.append("song", file);

    // Use centralized fetchApi which handles FormData correctly
    return fetchApi<{ message: string; filePath: string; orderId: number }>(
      endpoint,
      {
        method: "POST",
        body: formData,
      },
    );
  },

  /**
   * Delete the song file for an order
   */
  async deleteSong(orderId: number) {
    return fetchApi<void>(`/admin/songs/orders/${orderId}/song`, {
      method: "DELETE",
    });
  },

  /**
   * Upload an artwork file for an order
   */
  async uploadArtwork(orderId: number, file: File) {
    const endpoint = `/admin/songs/orders/${orderId}/artwork`;

    const formData = new FormData();
    formData.append("artwork", file);

    return fetchApi<{
      message: string;
      artworkFilePath: string;
      orderId: number;
    }>(endpoint, {
      method: "POST",
      body: formData,
    });
  },

  /**
   * Copy an order's song and artwork to a new demo entry (disabled by default)
   */
  async copyToDemo(orderId: number) {
    return fetchApi<Demo>(`/admin/songs/orders/${orderId}/copy-to-demo`, {
      method: "POST",
    });
  },
};
